import type { KeyboardEvent } from 'react'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { ClockIcon, XIcon } from '@phosphor-icons/react'
import { format, isValid } from 'date-fns'

export const TIME_PRESETS = [
  { label: '9 AM', value: '09:00' },
  { label: '12 PM', value: '12:00' },
  { label: '3 PM', value: '15:00' },
  { label: '5 PM', value: '17:00' },
]

/** Returns a copy of `base` (or today when null/invalid) at the given HH:mm. */
export function applyTimeToDate(base: Date | null, time: string): Date {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  const next = base && isValid(base) ? new Date(base) : new Date()
  next.setHours(hours, minutes, 0, 0)
  return next
}

/** Returns a copy of `date` at local midnight (the canonical "no time" value). */
export function stripTimeFromDate(date: Date): Date {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

/** Copies the time of day from `source` onto `target` (used to preserve a set time across date changes). */
export function copyTimeOfDay(target: Date, source: Date): Date {
  const next = new Date(target)
  next.setHours(source.getHours(), source.getMinutes(), 0, 0)
  return next
}

export function formatTimeOfDay(value: Date | string | null): string {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(String(value))
  if (!isValid(date)) return ''
  return format(date, 'h:mm a')
}

export interface DateTimeSectionProps {
  /** The date the time applies to; today is used when null. */
  date: Date | null
  /** Whether a time of day is currently set. */
  hasTime: boolean
  disabled?: boolean
  /**
   * Called with the full date+time to persist. `nextHasTime` false means the
   * time was removed (date is normalized to local midnight).
   */
  onTimeChange: (nextDate: Date, nextHasTime: boolean) => void
  className?: string
}

/**
 * Optional time-of-day editor shown at the bottom of the due/scheduled date
 * popovers. Collapsed to a single "Add time" row until the user opts in;
 * commits on preset click, Enter, or blur after an edit.
 */
export function DateTimeSection({
  date,
  hasTime,
  disabled = false,
  onTimeChange,
  className,
}: DateTimeSectionProps) {
  const persistedTime = hasTime && date && isValid(date) ? format(date, 'HH:mm') : null

  const [isExpanded, setIsExpanded] = useState(hasTime)
  const [timeValue, setTimeValue] = useState(persistedTime ?? '09:00')
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (persistedTime) {
      setIsExpanded(true)
      setTimeValue(persistedTime)
      setIsDirty(false)
    } else if (!hasTime) {
      setIsExpanded(false)
      setIsDirty(false)
    }
  }, [persistedTime, hasTime])

  const commitTime = (time: string) => {
    if (!time) return
    setTimeValue(time)
    setIsDirty(false)
    if (time === persistedTime) return
    onTimeChange(applyTimeToDate(date, time), true)
  }

  const removeTime = () => {
    setIsExpanded(false)
    setIsDirty(false)
    if (hasTime && date && isValid(date)) {
      onTimeChange(stripTimeFromDate(date), false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitTime(timeValue)
    }
  }

  if (!isExpanded) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsExpanded(true)}
        className={cn(
          'flex h-7 w-full items-center gap-2 rounded-md px-2 text-xs font-medium',
          'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <ClockIcon className="h-3.5 w-3.5 shrink-0" />
        Add time
      </button>
    )
  }

  return (
    <div className={cn('flex flex-col gap-1.5 px-0.5', className)}>
      <div className="flex items-center gap-1.5">
        <ClockIcon
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            hasTime ? 'text-primary' : 'text-muted-foreground',
          )}
        />
        <input
          type="time"
          value={timeValue}
          disabled={disabled}
          onChange={event => {
            setTimeValue(event.target.value)
            setIsDirty(true)
          }}
          onBlur={() => {
            if (isDirty) commitTime(timeValue)
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={removeTime}
          title="Remove time"
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            'text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex gap-1">
        {TIME_PRESETS.map(preset => {
          const active = hasTime && !isDirty && timeValue === preset.value
          return (
            <button
              key={preset.value}
              type="button"
              disabled={disabled}
              onClick={() => commitTime(preset.value)}
              className={cn(
                'flex-1 rounded-full border border-border bg-background px-1.5 py-0.5',
                'text-[11px] font-medium transition-colors hover:bg-muted',
                active && 'border-primary/40 bg-primary/10 text-primary',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {preset.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default DateTimeSection
